import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import './Auth.css';

const Auth = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('player');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);

  const { signUp, signIn, resetPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      if (isSignUp) {
        const { error, warning, needsEmailConfirmation } = await signUp(email, password, fullName, role);
        if (error) {
          setError(error.message || 'Failed to sign up');
        } else if (warning) {
          setMessage(warning);
          // Reset form
          setEmail('');
          setPassword('');
          setFullName('');
          setRole('player');
        } else if (needsEmailConfirmation) {
          setMessage('Check your email to confirm your account!');
          // Reset form
          setEmail('');
          setPassword('');
          setFullName('');
          setRole('player');
        } else {
          // User is signed in automatically (email confirmations disabled)
          setMessage('Account created successfully! Redirecting...');
          // Reset form
          setEmail('');
          setPassword('');
          setFullName('');
          setRole('player');
          // The AuthContext will handle the redirect via onAuthStateChange
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          setError(error.message || 'Failed to sign in');
        }
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img 
            src="/rollerCoaster_logo.svg" 
            alt="Client Roller Coaster Logo" 
            className="auth-logo"
          />
          <h1 className="auth-title">Client Roller Coaster</h1>
          <p className="auth-subtitle">
            {isSignUp ? 'Create your account' : 'Sign in to your account'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {isSignUp && (
            <div className="auth-field">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your name"
              />
            </div>
          )}

          <div className="auth-field">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>

          {!showResetPassword && (
            <div className="auth-field">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                minLength={6}
              />
              {!isSignUp && (
                <button
                  type="button"
                  onClick={() => setShowResetPassword(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#D702F6',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    marginTop: '0.5rem',
                    textDecoration: 'underline',
                    padding: 0
                  }}
                >
                  Forgot Password?
                </button>
              )}
            </div>
          )}

          {showResetPassword && (
            <div className="auth-field">
              <Label htmlFor="resetEmail">Enter your email to reset password</Label>
              <Input
                id="resetEmail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <Button
                  type="button"
                  onClick={async () => {
                    if (!email.trim()) {
                      setError('Please enter your email');
                      return;
                    }
                    setError('');
                    setMessage('');
                    setLoading(true);
                    const { error: resetError } = await resetPassword(email);
                    if (resetError) {
                      setError(resetError.message || 'Failed to send reset email');
                    } else {
                      setMessage('Password reset email sent! Check your inbox.');
                      setEmail('');
                    }
                    setLoading(false);
                  }}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? 'Sending...' : 'Send Reset Email'}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setShowResetPassword(false);
                    setError('');
                    setMessage('');
                    setEmail('');
                  }}
                  variant="outline"
                  style={{ flex: 1 }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div className="auth-role-block">
            <Label className="auth-role-label">Choose your role</Label>
            <div className="auth-role-options">
              <label className="auth-role-option">
                <input
                  type="radio"
                  name="role"
                  value="player"
                  checked={role === 'player'}
                  onChange={() => setRole('player')}
                />
                Player (join and participate)
              </label>
              <label className="auth-role-option">
                <input
                  type="radio"
                  name="role"
                  value="host"
                  checked={role === 'host'}
                  onChange={() => setRole('host')}
                />
                Host (create games; email verification required)
              </label>
            </div>
          </div>

          {error && (
            <div className="auth-error">
              {error}
            </div>
          )}

          {message && (
            <div className="auth-message">
              {message}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="auth-submit-button"
          >
            {loading ? 'Please wait...' : isSignUp ? 'Sign Up' : 'Sign In'}
          </Button>
        </form>

        <div className="auth-footer">
          <button
            type="button"
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError('');
              setMessage('');
              setRole('player');
            }}
            className="auth-toggle"
          >
            {isSignUp ? (
              <>Already have an account? <span>Sign In</span></>
            ) : (
              <>Don't have an account? <span>Sign Up</span></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Auth;

